import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Pencil } from 'lucide-react'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { AttachmentsPanel } from '@/components/crm/attachments-panel'
import { CommentsThread } from '@/components/crm/comments-thread'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { LayoutAwareDetail } from '@/components/crm/layout-aware-detail'
import { TagPicker } from '@/components/crm/tag-picker'
import { Timeline } from '@/components/crm/timeline'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/opportunities/$opportunityId')({
  component: OpportunityDetailPage,
})

type OpportunityRecord = {
  id: string
  name?: string | null
  accountId?: string | null
  pipelineId?: string | null
  stageId?: string | null
  amount?: string | null
  currency?: string | null
  probability?: number | null
  ownerUserId?: string | null
  closeReasonId?: string | null
  _ext?: Record<string, unknown>
}

type StageRow = {
  id: string
  pipelineId: string
  name: string
  isWon: boolean
  isLost: boolean
  sortOrder: number
}
type LookupRow = { id: string; name: string }

type ActivityRow = {
  id: string
  type?: string | null
  subject?: string | null
  dueAt?: string | Date | null
  completedAt?: string | Date | null
}

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function OpportunityDetailPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { opportunityId } = Route.useParams()

  const { data, isLoading } = useQuery(
    orpc.crm.opportunities.get.queryOptions({ input: { id: opportunityId } }),
  )

  const opportunity = data as OpportunityRecord | undefined

  const { data: stageData } = useQuery(
    orpc.crm.stages.list.queryOptions({
      input: { pipelineId: opportunity?.pipelineId ?? '00000000-0000-0000-0000-000000000000' },
      enabled: !!opportunity?.pipelineId,
    }),
  )
  const stages = (stageData as StageRow[] | undefined) ?? []
  const currentStage = useMemo(
    () => stages.find((s) => s.id === opportunity?.stageId),
    [stages, opportunity?.stageId],
  )

  const { data: closeReasonData } = useQuery(
    orpc.crm.lookups.list.queryOptions({ input: { table: 'close_reasons' } }),
  )
  const closeReasons = (closeReasonData as LookupRow[] | undefined) ?? []

  const { data: activityRows } = useQuery(
    orpc.crm.activities.list.queryOptions({
      input: {
        limit: 200,
        offset: 0,
        relatedToType: 'opportunity',
        relatedToId: opportunityId,
      },
    }),
  )

  const changeStageMutation = useMutation(
    orpc.crm.opportunities.changeStage.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.opportunity.changeStage'))
        queryClient.invalidateQueries({
          queryKey: orpc.crm.opportunities.get.queryKey({ input: { id: opportunityId } }),
        })
        queryClient.invalidateQueries({ queryKey: orpc.crm.opportunities.list.key() })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const updateMutation = useMutation(
    orpc.crm.opportunities.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.crm.opportunities.get.queryKey({ input: { id: opportunityId } }),
        })
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  if (isLoading || !opportunity) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const activities: ActivityRow[] = (activityRows as ActivityRow[] | undefined) ?? []
  const activityColumns: EntityListColumn<ActivityRow>[] = [
    {
      key: 'type',
      header: t('crm.activity.type'),
      render: (r) => <span className="capitalize">{r.type ?? '-'}</span>,
    },
    { key: 'subject', header: t('crm.activity.subject'), render: (r) => r.subject ?? '-' },
    { key: 'dueAt', header: t('crm.activity.dueAt'), render: (r) => formatDate(r.dueAt) },
    {
      key: 'completedAt',
      header: t('crm.activity.completedAt'),
      render: (r) =>
        r.completedAt ? (
          formatDate(r.completedAt)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
  ]

  const title = opportunity.name ?? '(unnamed opportunity)'
  const showCloseReason = currentStage?.isWon === true || currentStage?.isLost === true

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/crm/opportunities' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            {opportunity.amount && (
              <span>
                {opportunity.amount} {opportunity.currency ?? ''}
              </span>
            )}
            {typeof opportunity.probability === 'number' && <span>{opportunity.probability}%</span>}
            {opportunity.ownerUserId && (
              <span>
                {t('crm.common.ownedBy')}: {opportunity.ownerUserId}
              </span>
            )}
            {currentStage?.isWon && <Badge>{t('crm.opportunity.won')}</Badge>}
            {currentStage?.isLost && (
              <Badge variant="destructive">{t('crm.opportunity.lost')}</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('crm.opportunity.stage')}:</span>
            <Select
              value={opportunity.stageId ?? ''}
              onValueChange={(v) => changeStageMutation.mutate({ id: opportunityId, stageId: v })}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder={t('crm.opportunity.stage')} />
              </SelectTrigger>
              <SelectContent>
                {stages.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showCloseReason && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {t('crm.opportunity.closeReason')}:
              </span>
              <Select
                value={opportunity.closeReasonId ?? ''}
                onValueChange={(v) =>
                  updateMutation.mutate({ id: opportunityId, closeReasonId: v })
                }
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('crm.opportunity.closeReason')} />
                </SelectTrigger>
                <SelectContent>
                  {closeReasons.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: '/crm/opportunities/$opportunityId/edit',
                params: { opportunityId },
              })
            }
          >
            <Pencil className="h-4 w-4 mr-2" />
            {t('crm.common.edit')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">{t('navigation.details')}</TabsTrigger>
          <TabsTrigger value="timeline">{t('navigation.timeline')}</TabsTrigger>
          <TabsTrigger value="activities">{t('crm.activity.entityPlural')}</TabsTrigger>
          <TabsTrigger value="tags">{t('navigation.inputTags')}</TabsTrigger>
          <TabsTrigger value="comments">{t('navigation.chat')}</TabsTrigger>
          <TabsTrigger value="attachments">{t('navigation.fileDropzone')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <LayoutAwareDetail
            entityKey="opportunity"
            record={opportunity as Record<string, unknown> & { id: string }}
          />
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline recordType="opportunity" recordId={opportunityId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.activity.entityPlural')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityList rows={activities} columns={activityColumns} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.inputTags')}</CardTitle>
            </CardHeader>
            <CardContent>
              <TagPicker recordType="opportunity" recordId={opportunityId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.chat')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentsThread recordType="opportunity" recordId={opportunityId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.fileDropzone')}</CardTitle>
            </CardHeader>
            <CardContent>
              <AttachmentsPanel recordType="opportunity" recordId={opportunityId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
